const read = (callback, fallback = null) => {
  try {
    return callback() ?? fallback;
  } catch {
    return fallback;
  }
};

const dimension = (value, step = 200) =>
  Number.isFinite(value) && value >= 0 && value <= 100000 ? Math.round(value / step) * step : null;

const platformFamily = (value = '') => {
  if (/Android/i.test(value)) return 'android';
  if (/iPhone|iPad|iPod|iOS/i.test(value)) return 'ios';
  if (/Windows|Win32|Win64/i.test(value)) return 'windows';
  if (/CrOS|Chrome OS/i.test(value)) return 'chromeos';
  if (/Mac/i.test(value)) return 'macos';
  if (/Linux/i.test(value)) return 'linux';
  return null;
};

const environment = () => {
  if (typeof navigator === 'undefined') return { available: false, automated: null, headlessDeclared: null };
  const userAgent = read(() => navigator.userAgent, '');
  const brands = read(() => navigator.userAgentData.brands, []);
  const brandNames = read(
    () =>
      Array.isArray(brands)
        ? brands
            .slice(0, 10)
            .map((item) => item?.brand)
            .filter((brand) => typeof brand === 'string')
        : [],
    [],
  );
  const media = (query) => read(() => (typeof matchMedia === 'function' ? matchMedia(query).matches : null));
  const browserMatch = [
    ['Edg', /(?:Edg|EdgA|EdgiOS)\/(\d+)/],
    ['Opera', /OPR\/(\d+)/],
    ['Samsung', /SamsungBrowser\/(\d+)/],
    ['Firefox', /(?:Firefox|FxiOS)\/(\d+)/],
    ['Chrome', /(?:Chrome|CriOS|HeadlessChrome|HeadlessChromium)\/(\d+)/],
    ['Safari', /Version\/(\d+).*Safari\//],
  ]
    .map(([name, pattern]) => ({ name, version: pattern.exec(userAgent)?.[1] }))
    .find(({ version }) => version);
  const platform = read(() => navigator.userAgentData.platform) || read(() => navigator.platform, '');
  const uaPlatform = platformFamily(userAgent);
  const hintPlatform = platformFamily(read(() => navigator.userAgentData.platform, ''));
  const mobileHint = read(() => navigator.userAgentData.mobile);
  const width = read(() => window.innerWidth);
  const height = read(() => window.innerHeight);
  const outerWidth = read(() => window.outerWidth);
  const outerHeight = read(() => window.outerHeight);
  const screenWidth = read(() => window.screen.width);
  const screenHeight = read(() => window.screen.height);
  const availableWidth = read(() => window.screen.availWidth);
  const availableHeight = read(() => window.screen.availHeight);
  const pixelRatio = read(() => window.devicePixelRatio);
  const anomalies = [];
  if (uaPlatform && hintPlatform && uaPlatform !== hintPlatform) anomalies.push('platformDeclarationMismatch');
  if (typeof mobileHint === 'boolean' && userAgent && mobileHint !== /Mobile/i.test(userAgent))
    anomalies.push('mobileDeclarationMismatch');
  if (brandNames.includes('Chromium') && ['Firefox', 'Safari'].includes(browserMatch?.name))
    anomalies.push('browserDeclarationMismatch');
  if ((outerWidth > 0 && width > outerWidth + 200) || (outerHeight > 0 && height > outerHeight + 200))
    anomalies.push('viewportExceedsWindow');
  if (
    (screenWidth > 0 && availableWidth > screenWidth + 100) ||
    (screenHeight > 0 && availableHeight > screenHeight + 100)
  )
    anomalies.push('availableScreenExceedsScreen');
  if (screenWidth === 0 || screenHeight === 0) anomalies.push('zeroScreenDimension');
  const webdriver = read(() => navigator.webdriver);
  const touchPoints = read(() => navigator.maxTouchPoints);
  const boolean = (value) => (typeof value === 'boolean' ? value : null);
  return {
    available: true,
    automated: boolean(webdriver),
    headlessDeclared:
      userAgent || brandNames.length
        ? /\bHeadless(?:Chrome|Chromium)\//i.test(userAgent) ||
          brandNames.some((brand) => /^Headless(?:Chrome|Chromium)$/i.test(brand))
        : null,
    anomalies,
    touch: Number.isFinite(touchPoints) ? touchPoints > 0 : null,
    coarse: media('(pointer: coarse)'),
    hover: media('(hover: hover)'),
    reducedMotion: media('(prefers-reduced-motion: reduce)'),
    cookiesEnabled: boolean(read(() => navigator.cookieEnabled)),
    online: boolean(read(() => navigator.onLine)),
    userActivation: boolean(read(() => navigator.userActivation.hasBeenActive)),
    language: String(read(() => navigator.language, '')).slice(0, 20),
    browser: browserMatch?.name || 'other',
    browserVersion: browserMatch?.version || '',
    platform: String(platform).slice(0, 30),
    platformFamily: uaPlatform,
    clientHintPlatform: hintPlatform,
    mobileHint: boolean(mobileHint),
    viewport: dimension(width),
    viewportHeight: dimension(height),
    windowWidth: dimension(outerWidth),
    windowHeight: dimension(outerHeight),
    screenWidth: dimension(screenWidth),
    screenHeight: dimension(screenHeight),
    availableScreenWidth: dimension(availableWidth),
    availableScreenHeight: dimension(availableHeight),
    pixelRatio:
      Number.isFinite(pixelRatio) && pixelRatio > 0 && pixelRatio <= 16 ? Math.round(pixelRatio * 2) / 2 : null,
    timezoneOffset: new Date().getTimezoneOffset(),
    navigationType: read(() => performance.getEntriesByType?.('navigation')?.[0]?.type),
  };
};

export { environment };
